import * as React from 'react'

import {
  Alert,
  Box,
  Button,
  Card,
  Icon,
  Link,
  Loading,
  Text,
  Touchable,
  WizardStep,
  WizardStepRenderParams,
} from '@factorialco/deprecated-design-system'
import { PopoverMenu } from '@factorialco/deprecated-design-system'
import { F0Icon, type IconType } from '@factorialco/f0-react'
import {
  Desktop,
  HardDrive,
  Headset,
  Keyboard,
  Laptop,
  Mobile,
  Mouse,
  Settings,
} from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import { WizardData } from 'modules/employees/components/UnifiedAddEmployeeModal/types'

import { DeviceForm } from './DeviceForm'
import { ResetModalForm } from './ResetModalForm'
import { useDevicesManager } from './useDevicesManager'
import { useItAssetsInStockInventorySelector } from './useItAssetsInStockInventorySelector'

const getIconByDeviceType = (type: string): IconType => {
  const t = (type || '').toLowerCase()
  switch (t) {
    case 'laptop':
      return Laptop
    case 'phone':
      return Mobile
    case 'screen':
      return Desktop
    case 'mouse':
      return Mouse
    case 'keyboard':
      return Keyboard
    case 'headset':
      return Headset
    case 'accessory':
      return HardDrive
    case 'other':
      return Settings
    default:
      return Settings
  }
}

const DevicesStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const { assets: itAssetsInStock, loading: itAssetsLoading } =
    useItAssetsInStockInventorySelector()
  const [showResetConfirmation, setShowResetConfirmation] = React.useState(false)

  const {
    devices,
    showAddDeviceForm,
    selectedAssetId,
    editingDevice,
    equipmentLoading,
    hasRecommendedDevices,
    usedAssetIds,
    setShowAddDeviceForm,
    setSelectedAssetId,
    handleSaveDevice,
    handleEditDevice,
    handleCancelEdit,
    handleDeleteDevice,
    handleResetToDefault,
    nodeId,
    roleLevelName,
  } = useDevicesManager({
    data,
    onDataChanged,
    itAssetsInStock,
    onResetComplete: () => setShowResetConfirmation(false),
  })

  const hasRoleAssigned = nodeId
  const hasDevices = devices.length > 0
  const hasDevicesRecommended = hasRoleAssigned && hasRecommendedDevices
  const isRoleWithNotRecommendedDevices = hasRoleAssigned && !hasRecommendedDevices
  const isLoading = equipmentLoading || itAssetsLoading

  return (
    <>
      {isLoading && (
        <Box flexDirection="row" gap="s48">
          <Box flexDirection="column" gap="s16" width="s400">
            <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
              <Text.Header2>{i18n.t('add_employee_modal.devices_step.title')}</Text.Header2>
            </Box>
            <Loading />
          </Box>
        </Box>
      )}
      {!isLoading && (
        <Box flexDirection="row" gap="s48">
          <Box flexDirection="column" gap="s16" width="s400">
            <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
              <Text.Header2>{i18n.t('add_employee_modal.devices_step.title')}</Text.Header2>
              {nodeId && (
                <Link type="default">
                  <Box onClick={() => setShowResetConfirmation(true)}>
                    {i18n.t('add_employee_modal.devices_step.reset_to_default_equipment')}
                  </Box>
                </Link>
              )}
            </Box>

            {hasDevicesRecommended && (
              <Alert color="info">
                <Box flexDirection="column" gap="s8">
                  <Box flexDirection="row" alignItems="center" gap="s8">
                    <Text size="200">
                      <Icon.Medium icon="lightbulb" color="viridian1000" />
                    </Text>
                    <Text size="200" weight="semibold">
                      {i18n.t(
                        'add_employee_modal.devices_step.preselected_equipment_title_with_role',
                        {
                          roleLevelName,
                        }
                      )}
                    </Text>
                  </Box>
                  <Text size="200">
                    {i18n.t('add_employee_modal.devices_step.preselected_equipment_description')}
                  </Text>
                </Box>
              </Alert>
            )}

            <>
              <Box flexDirection="column" gap="s8">
                {!hasRoleAssigned && (
                  <Alert color="warning">
                    <Box flexDirection="column" gap="s8">
                      <Box flexDirection="row" alignItems="flexStart" gap="s8">
                        <Text size="200">
                          <Icon.Medium icon="alert-sign" color="moodNeutral" />
                        </Text>
                        <Text size="200" weight="semibold" color="textPrimary">
                          {i18n.t('add_employee_modal.devices_step.no_role_assigned_title')}
                        </Text>
                      </Box>
                      <Text size="200" color="textPrimary">
                        {i18n.t('add_employee_modal.devices_step.no_role_assigned_description')}
                      </Text>
                    </Box>
                  </Alert>
                )}

                {isRoleWithNotRecommendedDevices && (
                  <Alert color="warning">
                    <Box flexDirection="column" gap="s8">
                      <Box flexDirection="row" alignItems="flexStart" gap="s8">
                        <Text size="200">
                          <Icon.Medium icon="alert-sign" color="moodNeutral" />
                        </Text>
                        <Text size="200" weight="semibold" color="textPrimary">
                          {i18n.t('add_employee_modal.devices_step.no_default_devices_title', {
                            roleLevelName,
                          })}
                        </Text>
                      </Box>
                      <Text size="200" color="textPrimary">
                        {i18n.t('add_employee_modal.devices_step.no_default_devices_description')}
                      </Text>
                    </Box>
                  </Alert>
                )}

                {hasDevices &&
                  devices.map((device) => (
                    <Box key={device.id} flexDirection="column" gap="s8">
                      <Card>
                        <Card.Content>
                          <Box flexDirection="column" gap="s6" width="full">
                            <Box
                              flexDirection="row"
                              paddingTop="s6"
                              alignItems="center"
                              gap="s16"
                              width="full"
                            >
                              <Box flexDirection="row" alignItems="flexStart" gap="s8" flexGrow>
                                <Box
                                  alignItems="center"
                                  justifyContent="center"
                                  width="s32"
                                  height="s32"
                                  background="grey200"
                                  borderRadius={{ all: 'abs008' }}
                                >
                                  <F0Icon
                                    icon={getIconByDeviceType(device.type)}
                                    size="md"
                                    color="secondary"
                                  />
                                </Box>
                                <Box flexDirection="column" alignItems="flexStart" gap="s8">
                                  <Text size="200" weight="semibold">
                                    {device.brand} {device.name}
                                  </Text>
                                  <Text size="100" color="textSecondary">
                                    {device.type}
                                    {device.serialNumber ? ` • ${device.serialNumber}` : ''}
                                  </Text>
                                </Box>
                              </Box>
                              <Box expanded />
                              <PopoverMenu type="list">
                                <PopoverMenu.Trigger>
                                  <Button type="secondary" iconName="ellipsis" />
                                </PopoverMenu.Trigger>
                                <PopoverMenu.Content side="bottom" align="start" width="s140">
                                  <PopoverMenu.TouchableOption
                                    icon={'pencil'}
                                    title={i18n.t('common.edit')}
                                    onClick={() => handleEditDevice(device)}
                                  />
                                  <PopoverMenu.TouchableOption
                                    danger
                                    icon={'delete'}
                                    title={i18n.t('common.remove')}
                                    onClick={() => handleDeleteDevice(device.id)}
                                  />
                                </PopoverMenu.Content>
                              </PopoverMenu>
                            </Box>
                          </Box>
                          {!device.hasStock && editingDevice !== device && (
                            <Box width="full" paddingTop="s16" gap="s12">
                              <Alert color="error">
                                <Box flexDirection="column" gap="s8">
                                  <Box flexDirection="row" alignItems="flexStart" gap="s8">
                                    <Icon.Medium
                                      icon="alert-circle-line"
                                      color="moodSuperNegative"
                                    />
                                    <Text size="200" weight="semibold">
                                      {i18n.t('add_employee_modal.devices_step.out_of_stock')}
                                    </Text>
                                  </Box>
                                  <Text size="200">
                                    {i18n.t(
                                      'add_employee_modal.devices_step.out_of_stock_description'
                                    )}
                                  </Text>
                                  <Box flexDirection="row" gap="s8">
                                    <Button
                                      type="secondary"
                                      onClick={() => handleEditDevice(device)}
                                    >
                                      {i18n.t('add_employee_modal.devices_step.change_device')}
                                    </Button>
                                    <Button
                                      type="secondary"
                                      onClick={() => handleDeleteDevice(device.id)}
                                    >
                                      {i18n.t('common.remove')}
                                    </Button>
                                  </Box>
                                </Box>
                              </Alert>
                            </Box>
                          )}
                          {editingDevice === device && (
                            <Box paddingTop="s12" flexDirection="column" gap="s6" width="full">
                              <DeviceForm
                                selectedAssetId={selectedAssetId}
                                onAssetSelect={setSelectedAssetId}
                                onSave={handleSaveDevice}
                                onCancel={handleCancelEdit}
                                isEditing
                                usedAssetIds={usedAssetIds}
                                itAssets={
                                  device.hasStock
                                    ? itAssetsInStock
                                    : itAssetsInStock.filter((asset) => {
                                        const assetType =
                                          asset.itAssetModel?.typeName?.toLowerCase() || ''
                                        const deviceType = (device.type || '').toLowerCase()
                                        return assetType === deviceType
                                      })
                                }
                              />
                            </Box>
                          )}
                        </Card.Content>
                      </Card>
                    </Box>
                  ))}
              </Box>

              {!showAddDeviceForm && (
                <Box gap="s16">
                  <Touchable
                    onClick={() => {
                      setShowAddDeviceForm(true)
                    }}
                  >
                    <Button.Decorative iconName="add" type="secondary">
                      {i18n.t('add_employee_modal.devices_step.assign_device')}
                    </Button.Decorative>
                  </Touchable>
                </Box>
              )}

              {/* Add new device form */}
              {showAddDeviceForm && (
                <Box flexDirection="column" gap="s16" width="full">
                  <Card>
                    <Card.Content>
                      <Box flexDirection="row" alignItems="center" gap="s8">
                        <Box
                          alignItems="center"
                          justifyContent="center"
                          width="s32"
                          height="s32"
                          background="grey200"
                          borderRadius={{ all: 'abs008' }}
                        >
                          <Icon.Medium icon="add" color="textSecondary" />
                        </Box>
                        <Text size="200" weight="semibold">
                          {i18n.t('add_employee_modal.devices_step.select_device')}
                        </Text>
                      </Box>
                      <Box paddingTop="s8" flexDirection="column" gap="s6" width="full">
                        <DeviceForm
                          selectedAssetId={selectedAssetId}
                          onAssetSelect={setSelectedAssetId}
                          onSave={handleSaveDevice}
                          onCancel={handleCancelEdit}
                          isEditing={false}
                          usedAssetIds={usedAssetIds}
                          itAssets={itAssetsInStock}
                        />
                      </Box>
                    </Card.Content>
                  </Card>
                </Box>
              )}
            </>
          </Box>

          {/* Reset confirmation modal */}
          {showResetConfirmation && (
            <ResetModalForm
              onCancel={() => setShowResetConfirmation(false)}
              onConfirm={handleResetToDefault}
            />
          )}
        </Box>
      )}
    </>
  )
}

const isCompletedStep = (data: WizardData): boolean => {
  if (!data.selectedDevices || data.selectedDevices.length === 0) {
    return true
  }

  return data.validDevices === true
}

export const devicesStep = (): WizardStep<WizardData> => ({
  title: i18n.t('add_employee_modal.devices_step.title'),
  nextText: i18n.t('common.next'),
  render: (props) => <DevicesStep {...props} />,
  isCompleted: isCompletedStep,
})
